import { useState } from 'react'
import { useNavigate } from 'react-router'
import { childrenApi, ApiError } from '@/lib/children-api'
import { trackEvent, Events } from '@/lib/observability'
import { UpgradePrompt } from '@/components/billing'
import { WEEKDAYS, WEEKDAY_LABELS, type Weekday } from '@/types/child'

export function AddChildPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [currentChildCount, setCurrentChildCount] = useState(0)
  const [currentLimit, setCurrentLimit] = useState(1)
  const [hasSubscription, setHasSubscription] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [notes, setNotes] = useState('')
  const [attendanceDays, setAttendanceDays] = useState<Weekday[]>(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])

  // Parent fields
  const [parentName, setParentName] = useState('')
  const [parentEmail, setParentEmail] = useState('')
  const [parentPhone, setParentPhone] = useState('')

  const toggleDay = (day: Weekday) => {
    setAttendanceDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const parents = parentName && parentEmail
        ? [{ name: parentName, email: parentEmail, phone: parentPhone || undefined }]
        : undefined

      await childrenApi.create({
        firstName,
        lastName,
        dateOfBirth: dateOfBirth || undefined,
        notes: notes || undefined,
        attendanceDays,
        parents,
      })

      trackEvent(Events.CHILD_ADDED, {
        hasParentInvite: !!parentEmail,
        attendanceDaysCount: attendanceDays.length,
      })
      navigate('/children')
    } catch (err) {
      if (err instanceof ApiError) {
        // Check if this is a subscription limit error
        if (err.data?.error === 'subscription_required') {
          setCurrentChildCount(typeof err.data.used === 'number' ? err.data.used : 0)
          setCurrentLimit(typeof err.data.allowed === 'number' ? err.data.allowed : 1)
          setHasSubscription(err.data.hasSubscription === true)
          setShowUpgrade(true)
        } else {
          setError(err.message)
        }
      } else {
        setError('Failed to add child')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (showUpgrade) {
    return (
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="w-full max-w-md">
          <UpgradePrompt
            currentChildren={currentChildCount}
            currentLimit={currentLimit}
            hasSubscription={hasSubscription}
            onClose={() => {
              setShowUpgrade(false)
              navigate('/children')
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <button
        onClick={() => navigate('/children')}
        className="mb-4 text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Back to children
      </button>

      <h1 className="mb-4 text-2xl font-bold">Add Child</h1>

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <section>
          <h2 className="mb-3 text-lg font-medium">Child Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="firstName" className="mb-1 block text-sm font-medium">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="mb-1 block text-sm font-medium">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="mb-1 block text-sm font-medium">
                Date of Birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="notes" className="mb-1 block text-sm font-medium">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
                placeholder="Allergies, dietary requirements, etc."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Attendance Days
              </label>
              <p className="mb-2 text-xs text-gray-500">
                Which days does this child usually attend?
              </p>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    disabled={isLoading}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      attendanceDays.includes(day)
                        ? 'bg-primary-100 border-primary-500 text-primary-700'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {WEEKDAY_LABELS[day]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-lg font-medium">Parent/Guardian (Optional)</h2>
          <p className="mb-3 text-sm text-gray-500">
            You can add more parents after creating the child profile.
          </p>
          <div className="space-y-4">
            <div>
              <label htmlFor="parentName" className="mb-1 block text-sm font-medium">
                Parent Name
              </label>
              <input
                type="text"
                id="parentName"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="parentEmail" className="mb-1 block text-sm font-medium">
                Parent Email
              </label>
              <input
                type="email"
                id="parentEmail"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="parentPhone" className="mb-1 block text-sm font-medium">
                Parent Phone
              </label>
              <input
                type="tel"
                id="parentPhone"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                disabled={isLoading}
              />
            </div>
          </div>
        </section>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? 'Adding...' : 'Add Child'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/children')}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
